import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { adaptEchartsOptions, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { CustomChart, ScatterChart } from 'echarts/charts';
import { GridComponent, MarkAreaComponent, MarkLineComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { ECharts } from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { HOVER_VALUE_CHART_COLOR, type HistogramOverlay } from '../measures/measures-highlight.util';
import { MEASURE_COLORS, type MeasureId } from '../measures/measures.model';
import { buildOverlaySeries, fmt, type RenderApi } from '../chart-shared';
import {
  BOX_PLOT_ELEMENT_DEFS,
  DEFAULT_BOX_PLOT_DISPLAY,
  type BoxPlotDisplayConfig,
  type BoxPlotElementId,
  type BoxPlotParams,
} from './box-plot-chart.model';
import { computeBoxPlots, type BoxPlotSummary } from './box-plot-chart.util';

echarts.use([
  CustomChart,
  ScatterChart,
  SVGRenderer,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkAreaComponent,
]);

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

const COLOR_VIOLET = MEASURE_COLORS['q1']; // violet-500 #8b5cf6
const COLOR_VIOLET_FILL = 'rgba(139, 92, 246, 0.05)';
const COLOR_MEDIAN = MEASURE_COLORS['median']; // teal-500 #14b8a6
const COLOR_MEAN = MEASURE_COLORS['mean']; // rose-500 #f43f5e
const COLOR_GRAY = '#9ca3af'; // gray-400
const COLOR_OUTLIER = '#f43f5e'; // rose-500 (same as mean for consistency)

const DIM_OPACITY = 0.15;

// ---------------------------------------------------------------------------
// Highlight helpers
// ---------------------------------------------------------------------------

/** Convert a hex color to rgba with the given opacity. */
function applyOpacity(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** Return the base color when `elementId` is highlighted (or nothing highlighted), dimmed otherwise. */
function resolveColor(base: string, elementId: BoxPlotElementId, highlighted: BoxPlotElementId | null): string {
  if (highlighted === null) {
    return base;
  }
  return highlighted === elementId ? base : applyOpacity(base, DIM_OPACITY);
}

/** Boost line width when highlighted, unchanged otherwise. */
function resolveLineWidth(base: number, elementId: BoxPlotElementId, highlighted: BoxPlotElementId | null): number {
  if (highlighted === null) {
    return base;
  }
  return highlighted === elementId ? base * 1.5 : base;
}

@Component({
  selector: 'dojo-box-plot-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './box-plot-chart.component.html',
  styleUrl: './box-plot-chart.component.scss',
  host: {
    class: 'block w-full',
    '[class.h-full]': '!explainMode()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoxPlotChartComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  }

  private readonly isDark;
  private echartsInstance: ECharts | null = null;

  readonly initOpts = { renderer: 'svg' } as const;

  readonly params = input.required<BoxPlotParams>();
  readonly display = input<BoxPlotDisplayConfig>({});
  readonly overlay = input<HistogramOverlay>({ markLines: [], markAreas: [] });
  readonly hoveredValue = input<number | null>(null);
  readonly pinnedMeasures = input<ReadonlySet<MeasureId>>(new Set());
  readonly explainMode = input<boolean>(false);
  readonly showData = input<boolean>(false);

  readonly outlierSelect = output<number>();
  readonly measurePin = output<MeasureId>();
  readonly measureUnpin = output<MeasureId>();

  readonly highlightedElement = input<BoxPlotElementId | null>(null);

  readonly hideOutliers = signal(false);

  private readonly computedBoxPlots = computed(() => computeBoxPlots(this.params().columns));

  readonly chartOptions = computed<EChartsOption>(() => {
    const display = { ...DEFAULT_BOX_PLOT_DISPLAY, ...this.display() };
    const { categories, outlierData, summaries } = this.computedBoxPlots();
    const overlay = this.overlay();
    const showOutliers = !this.hideOutliers();
    const hoveredValue = this.hoveredValue();
    const isMulti = categories.length > 1;
    const highlighted = this.highlightedElement();
    const showData = this.showData();
    const xRange = computeXAxisRange(summaries, outlierData);
    const catIndices = summaries.map((_, i) => i);

    // Build all series with original category indices
    const boxPlotSeries = buildCustomBoxplotSeries(summaries, catIndices, isMulti, highlighted);
    const outlierSeries = buildOutlierSeries(showOutliers ? outlierData : [], display, hoveredValue, highlighted);
    const overlaySeries = buildOverlaySeries(overlay);

    const meanAndAnnotationSeries: Array<Record<string, unknown>> = [];
    for (let i = 0; i < summaries.length; i++) {
      meanAndAnnotationSeries.push(buildMeanSeries(summaries[i], i, highlighted));
      meanAndAnnotationSeries.push(buildAnnotationSeries(summaries[i], i, isMulti, highlighted));
    }

    // -----------------------------------------------------------------------
    // Two-grid layout when showData is true
    // -----------------------------------------------------------------------
    if (showData) {
      const dataLabels = categories.length === 1 ? ['Data'] : categories;
      const boxLabels = categories.length === 1 ? ['Box Plot'] : categories;

      const xAxisBase = {
        type: 'value' as const,
        ...xRange,
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { fontSize: 12, color: '#6b7280' },
      };

      const yAxisBase = {
        type: 'category' as const,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { fontSize: 11, color: '#6b7280' },
      };

      const toGrid = (s: Record<string, unknown>, idx: number) => ({
        ...s,
        xAxisIndex: idx,
        yAxisIndex: idx,
      });

      const dataSeries: Array<Record<string, unknown>> = [];
      for (let i = 0; i < this.params().columns.length; i++) {
        const summary = i < summaries.length ? summaries[i] : null;
        dataSeries.push(toGrid(buildDataPointsSeries(this.params().columns[i].data, i, summary, highlighted), 0));
      }

      return {
        tooltip: { show: true, trigger: 'item' },
        animationDurationUpdate: this.explainMode() ? 0 : undefined,
        grid: [
          // Grid 0 — Data dots (top, compact)
          { left: 55, right: 20, top: '1%', height: '10%' },
          // Grid 1 — Box plot (bottom, spacious — flush with data grid)
          { left: 55, right: 20, top: '11%', bottom: 55 },
        ],
        xAxis: [
          {
            ...xAxisBase,
            gridIndex: 0,
            axisLabel: { show: false },
            axisLine: { show: true, onZero: false, lineStyle: { color: '#e5e7eb' } },
          },
          {
            ...xAxisBase,
            gridIndex: 1,
            name: 'Value',
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: { fontSize: 12, color: '#6b7280' },
            axisLine: { lineStyle: { color: '#d1d5db' } },
          },
        ],
        yAxis: [
          { ...yAxisBase, data: dataLabels, gridIndex: 0 },
          { ...yAxisBase, data: boxLabels, gridIndex: 1 },
        ],
        series: [
          toGrid(boxPlotSeries, 1),
          toGrid(outlierSeries, 1),
          toGrid(overlaySeries, 1),
          ...dataSeries,
          ...meanAndAnnotationSeries.map((s) => toGrid(s, 1)),
        ],
      };
    }

    // -----------------------------------------------------------------------
    // Single-grid layout (default)
    // -----------------------------------------------------------------------
    return {
      tooltip: { show: false },
      animationDurationUpdate: this.explainMode() ? 0 : undefined,
      grid: {
        top: 5,
        bottom: isMulti ? 35 : 15,
        left: 5,
        right: 5,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Value',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        ...xRange,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { fontSize: 12, color: '#6b7280' },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: { show: categories.length > 1, fontSize: 11, color: '#6b7280' },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: isMulti, lineStyle: { color: '#e5e7eb' } },
      },
      series: [boxPlotSeries, outlierSeries, overlaySeries, ...meanAndAnnotationSeries],
    };
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  toggleOutliers(): void {
    this.hideOutliers.update((v) => !v);
  }

  onChartInit(instance: ECharts): void {
    this.echartsInstance = instance;
  }

  onChartClick(event: {
    seriesType?: string;
    seriesName?: string;
    dataIndex: number;
    data?: unknown;
    event?: { offsetX?: number };
  }): void {
    // Outlier scatter click
    if (event.seriesType === 'scatter' && event.seriesName === 'Outliers') {
      const data = event.data as [number, number];
      this.outlierSelect.emit(data[0]);
      return;
    }

    // Mean diamond click
    if (event.seriesType === 'scatter' && event.seriesName === 'Mean') {
      if (this.pinnedMeasures().has('mean')) {
        this.measureUnpin.emit('mean');
      } else {
        this.measurePin.emit('mean');
      }
      return;
    }

    // Custom boxplot click — find closest element from [min, Q1, median, Q3, max]
    if (event.seriesName === 'Box Plot' && this.echartsInstance) {
      const { summaries } = this.computedBoxPlots();
      const idx = event.dataIndex;
      if (idx < 0 || idx >= summaries.length) {
        return;
      }

      const pixelX = event.event?.offsetX;
      // eslint-disable-next-line eqeqeq -- intentional nullish check (null | undefined)
      if (pixelX == null) {
        return;
      }

      const [dataX] = this.echartsInstance.convertFromPixel({ gridIndex: 0 }, [pixelX, 0]) as [number, number];
      const s = summaries[idx];

      // Only allow clicks within the IQR box (Q1 to Q3)
      if (dataX < s.q1 || dataX > s.q3) {
        return;
      }

      const elements: Array<{ id: MeasureId; value: number }> = [
        { id: 'q1', value: s.q1 },
        { id: 'median', value: s.median },
        { id: 'q3', value: s.q3 },
      ];

      let closest = elements[0];
      for (const el of elements) {
        if (Math.abs(dataX - el.value) < Math.abs(dataX - closest.value)) {
          closest = el;
        }
      }

      if (this.pinnedMeasures().has(closest.id)) {
        this.measureUnpin.emit(closest.id);
      } else {
        this.measurePin.emit(closest.id);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Axis range
// ---------------------------------------------------------------------------

function computeXAxisRange(
  summaries: BoxPlotSummary[],
  outlierData: Array<[number, number]>,
): { min: number; max: number } {
  if (summaries.length === 0) {
    return { min: 0, max: 1 };
  }

  let dataMin = Infinity;
  let dataMax = -Infinity;

  for (const s of summaries) {
    if (s.min < dataMin) {
      dataMin = s.min;
    }
    if (s.max > dataMax) {
      dataMax = s.max;
    }
  }
  for (const [, value] of outlierData) {
    if (value < dataMin) {
      dataMin = value;
    }
    if (value > dataMax) {
      dataMax = value;
    }
  }

  const range = dataMax - dataMin || 1;
  const padding = range * 0.1;
  return { min: dataMin - padding, max: dataMax + padding };
}

// ---------------------------------------------------------------------------
// Custom box plot — each element gets its own color
// ---------------------------------------------------------------------------

/**
 * Custom-rendered box plot with color-coded elements:
 *   - IQR box:        purple stroke + subtle fill
 *   - Median line:     sky blue
 *   - Whisker lines:   gray
 *   - Whisker caps:    gray
 *
 * Data format per item: [categoryIndex, min, Q1, median, Q3, max]
 */
function buildCustomBoxplotSeries(
  summaries: BoxPlotSummary[],
  categoryIndices: number[],
  isMulti: boolean,
  highlighted: BoxPlotElementId | null,
): Record<string, unknown> {
  return {
    type: 'custom',
    name: 'Box Plot',
    data: summaries.map((s, i) => [categoryIndices[i], s.min, s.q1, s.median, s.q3, s.max]),
    encode: { y: 0, x: [1, 2, 3, 4, 5] },
    z: 2,
    renderItem: (_params: { dataIndex: number }, api: RenderApi) => {
      const catIdx = api.value(0);
      const min = api.value(1);
      const q1 = api.value(2);
      const med = api.value(3);
      const q3 = api.value(4);
      const max = api.value(5);

      const minPt = api.coord([min, catIdx]);
      const q1Pt = api.coord([q1, catIdx]);
      const medPt = api.coord([med, catIdx]);
      const q3Pt = api.coord([q3, catIdx]);
      const maxPt = api.coord([max, catIdx]);

      const bandHeight = api.size!([0, 1])[1];
      const boxHalf = Math.min(bandHeight * 0.28, 28);
      const capHalf = Math.min(bandHeight * 0.14, 16);

      const boxFill = highlighted === null || highlighted === 'iqr-box' ? COLOR_VIOLET_FILL : 'transparent';

      return {
        type: 'group',
        children: [
          // --- Whisker connecting lines ---
          line(
            minPt[0],
            minPt[1],
            q1Pt[0],
            q1Pt[1],
            resolveColor(COLOR_GRAY, 'lower-whisker', highlighted),
            resolveLineWidth(1.5, 'lower-whisker', highlighted),
          ),
          line(
            q3Pt[0],
            q3Pt[1],
            maxPt[0],
            maxPt[1],
            resolveColor(COLOR_GRAY, 'upper-whisker', highlighted),
            resolveLineWidth(1.5, 'upper-whisker', highlighted),
          ),

          // --- Whisker caps ---
          line(
            minPt[0],
            minPt[1] - capHalf,
            minPt[0],
            minPt[1] + capHalf,
            resolveColor(COLOR_GRAY, 'minimum', highlighted),
            resolveLineWidth(1.5, 'minimum', highlighted),
          ),
          line(
            maxPt[0],
            maxPt[1] - capHalf,
            maxPt[0],
            maxPt[1] + capHalf,
            resolveColor(COLOR_GRAY, 'maximum', highlighted),
            resolveLineWidth(1.5, 'maximum', highlighted),
          ),

          // --- IQR box (purple) ---
          {
            type: 'rect',
            shape: {
              x: q1Pt[0],
              y: q1Pt[1] - boxHalf,
              width: q3Pt[0] - q1Pt[0],
              height: boxHalf * 2,
              r: 4,
            },
            style: {
              fill: boxFill,
              stroke: resolveColor(COLOR_VIOLET, 'iqr-box', highlighted),
              lineWidth: resolveLineWidth(2, 'iqr-box', highlighted),
            },
          },

          // --- Q1 edge line (stands out when 'q1' is highlighted) ---
          line(
            q1Pt[0],
            q1Pt[1] - boxHalf,
            q1Pt[0],
            q1Pt[1] + boxHalf,
            resolveColor(COLOR_VIOLET, 'q1', highlighted),
            resolveLineWidth(2, 'q1', highlighted),
          ),

          // --- Q3 edge line (stands out when 'q3' is highlighted) ---
          line(
            q3Pt[0],
            q3Pt[1] - boxHalf,
            q3Pt[0],
            q3Pt[1] + boxHalf,
            resolveColor(COLOR_VIOLET, 'q3', highlighted),
            resolveLineWidth(2, 'q3', highlighted),
          ),

          // --- Median line ---
          line(
            medPt[0],
            medPt[1] - boxHalf,
            medPt[0],
            medPt[1] + boxHalf,
            resolveColor(COLOR_MEDIAN, 'median', highlighted),
            resolveLineWidth(2.5, 'median', highlighted),
          ),

          // --- IQR bracket arrows (purple, hidden in multi-column) ---
          ...(isMulti
            ? []
            : iqrBracket(q1Pt[0], q3Pt[0], q1Pt[1] + boxHalf + 37, resolveColor(COLOR_VIOLET, 'iqr-box', highlighted))),
        ],
      };
    },
    tooltip: { show: false },
  };
}

/** Shorthand for a line graphic element. */
function line(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  lineWidth: number,
): Record<string, unknown> {
  return {
    type: 'line',
    shape: { x1, y1, x2, y2 },
    style: { stroke, lineWidth },
  };
}

/** IQR bracket: two arrow lines from Q1->center and center->Q3, with a gap for the text label. */
function iqrBracket(leftX: number, rightX: number, y: number, color: string): Array<Record<string, unknown>> {
  const midX = (leftX + rightX) / 2;
  const textGap = 30; // half-width gap for the text label
  const arrow = 5;

  return [
    // Left line segment: Q1 to (mid - gap)
    line(leftX, y, midX - textGap, y, color, 1.5),
    // Right line segment: (mid + gap) to Q3
    line(midX + textGap, y, rightX, y, color, 1.5),
    // Left arrowhead at Q1
    line(leftX, y, leftX + arrow, y - arrow, color, 1.5),
    line(leftX, y, leftX + arrow, y + arrow, color, 1.5),
    // Right arrowhead at Q3
    line(rightX, y, rightX - arrow, y - arrow, color, 1.5),
    line(rightX, y, rightX - arrow, y + arrow, color, 1.5),
  ];
}

// ---------------------------------------------------------------------------
// Outlier scatter
// ---------------------------------------------------------------------------

function buildOutlierSeries(
  outlierData: Array<[number, number]>,
  display: BoxPlotDisplayConfig,
  hoveredValue: number | null,
  highlighted: BoxPlotElementId | null,
): Record<string, unknown> {
  const color = display.outlierColor ?? COLOR_OUTLIER;
  const effectiveColor = resolveColor(color, 'outliers', highlighted);

  return {
    name: 'Outliers',
    type: 'scatter',
    data: outlierData.map(([catIdx, value]) => ({
      value: [value, catIdx],
      itemStyle: hoveredValue !== null && value === hoveredValue ? { color: HOVER_VALUE_CHART_COLOR } : undefined,
    })),
    itemStyle: { color: effectiveColor },
    symbolSize: 8,
    z: 5,
    label: {
      show: true,
      position: 'top',
      fontSize: 10,
      color: effectiveColor,
      formatter: (p: unknown) => fmt((p as { value: number[] }).value[0]),
    },
    tooltip: { show: false },
  };
}

// ---------------------------------------------------------------------------
// Mean diamond
// ---------------------------------------------------------------------------

function buildMeanSeries(
  summary: BoxPlotSummary,
  categoryIndex: number,
  highlighted: BoxPlotElementId | null,
): Record<string, unknown> {
  const color = resolveColor(COLOR_MEAN, 'mean', highlighted);

  return {
    name: 'Mean',
    type: 'scatter',
    data: [[summary.mean, categoryIndex]],
    symbol: 'diamond',
    symbolSize: highlighted === 'mean' ? 16 : 12,
    z: 10,
    itemStyle: { color },
    tooltip: { show: false },
    label: {
      show: true,
      position: 'top',
      offset: [0, -34],
      fontSize: 11,
      fontWeight: 500,
      color,
      formatter: () => `Mean: ${fmt(summary.mean)}`,
    },
  };
}

// ---------------------------------------------------------------------------
// Raw data points (strip plot above the box)
// ---------------------------------------------------------------------------

const COLOR_DATA_DOT = '#6b7280'; // gray-500
const COLOR_DATA_DIM = `rgba(107, 114, 128, 0.15)`; // gray-500 at DIM_OPACITY

/** Determine which BoxPlotElementIds a raw data value belongs to. */
function classifyDataPoint(value: number, s: BoxPlotSummary): BoxPlotElementId[] {
  if (s.outliers.includes(value)) {
    return ['outliers'];
  }
  if (value <= s.min) {
    return ['minimum', 'lower-whisker'];
  }
  if (value >= s.max) {
    return ['maximum', 'upper-whisker'];
  }
  if (value < s.q1) {
    return ['lower-whisker'];
  }
  if (value > s.q3) {
    return ['upper-whisker'];
  }
  // Inside IQR — belongs to iqr-box, plus special values
  const ids: BoxPlotElementId[] = ['iqr-box'];
  if (value === s.median) {
    ids.push('median');
  }
  if (value === s.q1) {
    ids.push('q1');
  }
  if (value === s.q3) {
    ids.push('q3');
  }
  return ids;
}

/** Get the highlight color for a data dot based on the active explain element. */
function dataDotColor(value: number, s: BoxPlotSummary | null, highlighted: BoxPlotElementId | null): string {
  if (highlighted === null || s === null) {
    return COLOR_DATA_DOT;
  }
  const elements = classifyDataPoint(value, s);
  if (elements.includes(highlighted)) {
    const def = BOX_PLOT_ELEMENT_DEFS.find((d) => d.id === highlighted);
    return def?.color ?? COLOR_DATA_DOT;
  }
  return COLOR_DATA_DIM;
}

function buildDataPointsSeries(
  rawData: number[],
  categoryIndex: number,
  summary: BoxPlotSummary | null,
  highlighted: BoxPlotElementId | null,
): Record<string, unknown> {
  return {
    name: 'Data',
    type: 'scatter',
    data: rawData.map((v) => ({
      value: [v, categoryIndex],
      itemStyle: {
        color: dataDotColor(v, summary, highlighted),
        opacity: highlighted !== null ? 1 : 0.5,
      },
    })),
    symbolSize: 9,
    z: 1,
    itemStyle: { color: COLOR_DATA_DOT, opacity: 0.5 },
    label: { show: false },
    emphasis: { scale: 1.6 },
    tooltip: {
      show: true,
      formatter: (p: unknown) => `${fmt((p as { value: number[] }).value[0])}`,
    },
  };
}

// ---------------------------------------------------------------------------
// Annotation labels
// ---------------------------------------------------------------------------

function buildAnnotationSeries(
  summary: BoxPlotSummary,
  categoryIndex: number,
  isMulti: boolean,
  highlighted: BoxPlotElementId | null,
): Record<string, unknown> {
  const y = categoryIndex;

  const data: Array<Record<string, unknown>> = [
    // --- Above the box: Median ---
    {
      value: [summary.median, y],
      label: {
        show: true,
        position: 'top',
        offset: [0, -27],
        fontSize: 11,
        fontWeight: 500,
        color: resolveColor(COLOR_MEDIAN, 'median', highlighted),
        formatter: () => `Median: ${fmt(summary.median)}`,
      },
    },
    // --- Below box: Q1, Q3 (purple, well clear of the box) ---
    {
      value: [summary.q1, y],
      label: {
        show: true,
        position: 'bottom',
        offset: [0, 37],
        fontSize: 11,
        fontWeight: 500,
        color: resolveColor(COLOR_VIOLET, 'q1', highlighted),
        formatter: () => `Q\u2081: ${fmt(summary.q1)}`,
      },
    },
    {
      value: [summary.q3, y],
      label: {
        show: true,
        position: 'bottom',
        offset: [0, 37],
        fontSize: 11,
        fontWeight: 500,
        color: resolveColor(COLOR_VIOLET, 'q3', highlighted),
        formatter: () => `Q\u2083: ${fmt(summary.q3)}`,
      },
    },
    // --- Below whisker caps: Minimum / Maximum (gray, close to caps) ---
    {
      value: [summary.min, y],
      label: {
        show: true,
        position: 'bottom',
        offset: [0, 11],
        fontSize: 11,
        fontWeight: 500,
        color: resolveColor('#9ca3af', 'minimum', highlighted),
        formatter: () => `Min: ${fmt(summary.min)}`,
      },
    },
    {
      value: [summary.max, y],
      label: {
        show: true,
        position: 'bottom',
        offset: [0, 11],
        fontSize: 11,
        fontWeight: 500,
        color: resolveColor('#9ca3af', 'maximum', highlighted),
        formatter: () => `Max: ${fmt(summary.max)}`,
      },
    },
  ];

  // IQR label and whisker explanations only in single-column mode
  if (!isMulti) {
    data.push(
      {
        value: [(summary.q1 + summary.q3) / 2, y],
        label: {
          show: true,
          position: 'bottom',
          offset: [0, 53],
          fontSize: 11,
          fontWeight: 500,
          color: resolveColor(COLOR_VIOLET, 'iqr-box', highlighted),
          formatter: () => `IQR: ${fmt(summary.iqr)}`,
        },
      },
      {
        value: [summary.min, y],
        label: {
          show: true,
          position: 'bottom',
          offset: [0, 26],
          fontSize: 9,
          fontWeight: 400,
          color: resolveColor('#9ca3af', 'minimum', highlighted),
          formatter: () => `(Min value in data \u2265 Q\u2081 \u2212 1.5\u00b7IQR)`,
        },
      },
      {
        value: [summary.max, y],
        label: {
          show: true,
          position: 'bottom',
          offset: [0, 26],
          fontSize: 9,
          fontWeight: 400,
          color: resolveColor('#9ca3af', 'maximum', highlighted),
          formatter: () => `(Max value in data \u2264 Q\u2083 + 1.5\u00b7IQR)`,
        },
      },
    );
  }

  return {
    type: 'scatter',
    symbolSize: 0,
    silent: true,
    tooltip: { show: false },
    data,
  };
}
